import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  MinusCircleOutlined,
  PlusOutlined,
  UserOutlined,
  LockOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
  CheckCircleTwoTone,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from 'react-query';
import {
  Button,
  Form,
  Input,
  message,
  Space,
  Collapse,
  Row,
  Typography,
  Checkbox,
  Radio,
  Divider,
  Tooltip,
  Popconfirm,
} from 'antd';
import { createWebhook, verifyWebhook, editWebhook } from '@api/notificationChannel';
import {
  StyledWrapper,
  StyledStandardFieldsWrapper,
  StyledInlineElements,
  StyledAuthFields,
  StyledField,
} from '../styled';
import EmailInput from './FormElement/EmailList';
import { standardAIData, AuthType } from '../constant';
import {
  getAIStandardData,
  getAICustomData,
  getMAStandardData,
  getMAOpsData,
  filterEditedAIStandardFields,
  getFriendlyErrorMessage,
} from '../utils';

const { Title } = Typography;
const { Panel } = Collapse;
const baseUrl = window.conviva.baseUrl;

const WebhooksForm = ({ webhook }) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const [showStandardFields, setShowStandardFields] = useState(false);
  const [verifiedWebhookStatus, setVerifiedWebhookStatus] = useState(false);
  const [cancelPopOpenStatus, setCancelPopOpenStatus] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isVerifyError, setIsVerifyError] = useState(false);
  const [isVerifySuccess, setIsVerifySuccess] = useState(false);
  const [verifyErrorMessage, setVerifyErrorMessage] = useState('');
  const [showAuthOptions, setShowAuthOptions] = useState(false);
  const [authType, setAuthType] = useState(AuthType.credential);
  const [editMode, setEditMode] = useState(false);
  const [form] = Form.useForm();

  const customKeyPattern = /^[a-zA-Z0-9_]+$/;

  useEffect(() => {
    if (isFormChanged) {
      setVerifiedWebhookStatus(false);
    }
  }, [isFormChanged]);

  useEffect(() => {
    if (Object.keys(webhook).length) {
      setEditMode(true);
      form.setFieldsValue({
        name: webhook.name,
        emails: webhook.emails,
        url: webhook.webhookUrl,
      });
      if (webhook.authentication) {
        setShowAuthOptions(true);
        if (webhook.authentication.type === AuthType.credential) {
          setAuthType(AuthType.credential);
          form.setFieldsValue({
            auth: true,
            username: webhook.authentication.username,
            password: webhook.authentication.tokenOrPassword,
          });
        } else {
          setAuthType(AuthType.token);
          form.setFieldsValue({
            auth: true,
            token: webhook.authentication.tokenOrPassword,
          });
        }
      }
      if (webhook.config.aiConfig) {
        const alertAIFields = webhook.config.aiConfig.alertsFields;
        const alertsFieldsRefactored = filterEditedAIStandardFields(alertAIFields);
        const customAIFields = webhook.config.aiConfig.customFields;
        const customFieldsRefactored = Object.keys(customAIFields).map((key) => {
          return {
            customKey: key,
            customValue: customAIFields[key],
          };
        });
        form.setFieldsValue({
          alertAIFields: alertsFieldsRefactored,
          customFields: customFieldsRefactored,
        });
      }
    } else {
      setEditMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webhook]);

  const verifyWebhookMutation = useMutation(verifyWebhook, {
    onError: (e) => {
      setIsVerifyError(true);
      setVerifiedWebhookStatus(false);
      const errorMessage = getFriendlyErrorMessage(e);
      setVerifyErrorMessage(errorMessage);
      setIsVerifySuccess(false);
    },
    onSuccess: () => {
      setIsFormChanged(false);
      setIsVerifyError(false);
      setIsVerifySuccess(true);
      setVerifiedWebhookStatus(true);
    },
  });

  const createWebhooksMutation = useMutation(createWebhook, {
    onSuccess: () => {
      queryClient.invalidateQueries('webhooksListData');
    },
  });

  const editWebhooksMutation = useMutation(editWebhook, {
    onSuccess: () => {
      queryClient.invalidateQueries('webhooksListData');
    },
  });

  const onFinish = async () => {
    message.success('Submit success!');
    setIsFormChanged(false);
    try {
      const values = await form.validateFields([
        'name',
        'url',
        'emails',
        'auth',
        'alertAIFields',
        'customFields',
      ]);
      let authValues;
      if (values.auth) {
        if (authType === AuthType.credential) {
          const authFormData = await form.validateFields(['username', 'password']);
          authValues = {
            type: AuthType.credential,
            username: authFormData.username,
            tokenOrPassword: authFormData.password,
          };
        } else {
          await form.validateFields(['token']);
          authValues = {
            type: AuthType.token,
            tokenOrPassword: values.token,
          };
        }
      } else {
        authValues = false;
      }
      const alertAIFieldsRefactored = getAIStandardData(values.alertAIFields);
      const customFieldsRefactored = getAICustomData(values.customFields || []);
      const alertMAFieldsRefactored = !editMode
        ? getMAStandardData({})
        : webhook.config.maConfig.alertFields;
      const opsMAFieldsRefactored = !editMode
        ? getMAOpsData({})
        : webhook.config.maConfig.maOpsFields;
      const customMAFieldsRefactored = !editMode ? {} : webhook.config.maConfig.customFields;
      const payload = {
        name: values.name,
        webhookUrl: values.url,
        emails: values.emails,
        alertType: 'AI',
        context: 'app',
        product: 'ae',
        authentication: authValues,
        config: {
          aiConfig: {
            alertFields: alertAIFieldsRefactored,
            customFields: customFieldsRefactored,
          },
          maConfig: {
            alertFields: alertMAFieldsRefactored,
            maOpsFields: opsMAFieldsRefactored,
            customFields: customMAFieldsRefactored,
          },
        },
      };
      if (editMode) {
        await editWebhooksMutation.mutateAsync({ payload, id: webhook.webhookId });
      } else {
        await createWebhooksMutation.mutateAsync(payload);
      }
      history.push(`${baseUrl}/settings/webhooks`);
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  const handleAuthTypeChange = async (value) => {
    setAuthType(value);
    if (value === AuthType.credential) {
      await form.resetFields(['token']);
    } else {
      await form.resetFields(['username', 'password']);
    }
    setIsFormChanged(true);
  };

  const handleVerifyWebhook = async () => {
    setIsVerifyError(false);
    setVerifyErrorMessage('');
    try {
      const values = await form.validateFields([
        'name',
        'url',
        'auth',
        'alertAIFields',
        'customFields',
      ]);
      let authValues;
      if (values.auth) {
        if (authType === AuthType.credential) {
          const authFormData = await form.validateFields(['username', 'password']);
          authValues = {
            type: AuthType.credential,
            username: authFormData.username,
            tokenOrPassword: authFormData.password,
          };
        } else {
          await form.validateFields(['token']);
          authValues = {
            type: AuthType.token,
            tokenOrPassword: values.token,
          };
        }
      } else {
        authValues = false;
      }
      const alertAIFieldsRefactored = getAIStandardData(values.alertAIFields);
      const customFieldsRefactored = getAICustomData(values.customFields || []);
      const payload = {
        name: values.name,
        webhookUrl: values.url,
        alertType: 'AI',
        authentication: authValues,
        config: {
          aiConfig: {
            alertFields: alertAIFieldsRefactored,
            customFields: customFieldsRefactored,
          },
        },
      };
      verifyWebhookMutation.mutate(payload);
    } catch (error) {
      setIsVerifyError(true);
      setIsVerifySuccess(false);
      console.error('Validation error:', error);
    }
  };

  const onFinishFailed = () => {
    message.error('Submit failed!');
  };

  const handleFormChange = () => {
    setIsFormChanged(true);
  };

  const showFields = () => {
    setShowStandardFields(!showStandardFields);
  };

  const layout = {
    labelCol: { span: 4 },
    wrapperCol: { span: 20 },
  };

  const ErrorDescription = ({ message }) => {
    return (
      <div>
        <p>{message}</p>
      </div>
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setCancelPopOpenStatus(newOpen);
      return;
    }
    if (!isFormChanged) {
      handleConfirmPopConfirm();
    } else {
      setCancelPopOpenStatus(newOpen);
    }
  };

  const handleConfirmPopConfirm = () => {
    history.push(`${baseUrl}/settings/webhooks`);
  };

  const handleCancelPopConfirm = () => {
    setCancelPopOpenStatus(false);
  };

  return (
    <StyledWrapper>
      <Title level={3}> Webhook Configuration</Title>
      <Form
        {...layout}
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        onValuesChange={handleFormChange}
      >
        <Form.Item
          name="name"
          label="Webhook Name"
          rules={[
            { required: true },
            {
              pattern: /^[a-zA-Z0-9][a-zA-Z0-9\s\W]*/,
              message: 'Invalid Input. Start with alphanumeric character.',
            },
            {
              min: 3,
              max: 256,
              message: '3 to 256 characters. Start with alphanumeric character.',
            },
          ]}
        >
          <Input placeholder="3 to 256 characters. Start with alphanumeric character." />
        </Form.Item>
        <Form.Item
          label="Emails"
          name="emails"
          rules={[
            {
              required: true,
              message: 'Please enter at least one email!',
            },
          ]}
        >
          <EmailInput emails={webhook.emails} />
        </Form.Item>
        <Form.Item
          name="url"
          label="URL"
          rules={[{ required: true }, { type: 'url', message: 'Invalid URL' }]}
        >
          <Input placeholder="Webhook URL" />
        </Form.Item>
        <StyledAuthFields>
          <Form.Item name="auth" valuePropName="checked">
            <Checkbox onChange={() => setShowAuthOptions(!showAuthOptions)}>
              URL has Authentication <span>(username & password / authorization token)</span>
            </Checkbox>
          </Form.Item>
          {showAuthOptions ? (
            <Radio.Group onChange={(e) => handleAuthTypeChange(e.target.value)} value={authType}>
              <StyledInlineElements>
                <Radio value={AuthType.credential}></Radio>
                <Form.Item
                  name="username"
                  rules={[
                    { required: true, message: 'Please add username!' },
                    { max: 256, message: 'Maximum 256 characters.' },
                    { min: 3, message: 'Minimum 3 characters.' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="site-form-item-icon" />}
                    placeholder="Username"
                    disabled={authType === AuthType.token}
                  />
                </Form.Item>
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: 'Please add password!' },
                    { max: 256, message: 'Maximum 256 characters.' },
                  ]}
                >
                  <Input
                    prefix={<LockOutlined className="site-form-item-icon" />}
                    type="password"
                    placeholder="Password"
                    disabled={authType === AuthType.token}
                  />
                </Form.Item>
              </StyledInlineElements>
              <StyledInlineElements>
                <Radio value={AuthType.token}></Radio>
                <Form.Item
                  name="token"
                  rules={[{ required: true }, { max: 256, message: 'Maximum 256 characters.' }]}
                >
                  <Input placeholder="Token" disabled={authType === AuthType.credential} />
                </Form.Item>
              </StyledInlineElements>
            </Radio.Group>
          ) : null}
        </StyledAuthFields>
        <Collapse defaultActiveKey={['1']}>
          <Panel header={<div>Configure AI Alerts payload.</div>} key="1">
            <Form.Item>
              <p>A standard set of payload keys and values are included.</p>
              <Button onClick={() => showFields()}>View Standard Fields</Button>
            </Form.Item>
            {showStandardFields ? (
              <StyledStandardFieldsWrapper>
                <Row className="standard-field-row">
                  <StyledField>
                    Key{' '}
                    <Tooltip title="The keyname for the value field asset in Pulse.Greyed out keys are defined by Pulse and non-editable.">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </StyledField>
                  <StyledField>
                    Value (Example){' '}
                    <Tooltip title="The corresponding value for the key asset by the customer in Pulse or generated by Conviva as part of the alert condition.">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </StyledField>
                  <StyledField>
                    Customize Key Name{' '}
                    <Tooltip title="Optional fields, that allow for customization of key name.">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </StyledField>
                </Row>
                {standardAIData.map((data, index) => {
                  return (
                    <Row key={index} className="standard-field-row">
                      <StyledField>
                        <Input placeholder={data.key} disabled />
                      </StyledField>
                      <StyledField>
                        <Input placeholder={`${data.value}`} disabled />
                      </StyledField>
                      <Form.Item
                        name={['alertAIFields', data.key]}
                        rules={[
                          {
                            pattern: customKeyPattern,
                            message: 'Only Alphanumeric and _ is allowed.',
                          },
                        ]}
                      >
                        <Input placeholder={data.exampleText} />
                      </Form.Item>
                    </Row>
                  );
                })}
              </StyledStandardFieldsWrapper>
            ) : null}
            <Form.List name="customFields">
              {(fields, { add, remove }) => (
                <>
                  <Form.Item>
                    <p>You may customize this payload by adding custom fields.</p>
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                      Add Custom Field(s)
                    </Button>
                  </Form.Item>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'customKey']}
                        rules={[
                          { required: true, message: 'Missing Custom Key' },
                          {
                            pattern: customKeyPattern,
                            message: 'Only Alphanumeric and _ is allowed.',
                          },
                        ]}
                      >
                        <Input placeholder="Custom Key" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'customValue']}
                        rules={[
                          { required: true, message: 'Missing Custom Value' },
                          {
                            pattern: customKeyPattern,
                            message: 'Only Alphanumeric and _ is allowed.',
                          },
                        ]}
                      >
                        <Input placeholder="Custom Value" />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                </>
              )}
            </Form.List>
            <Space
              align="center"
              direction="horizontal"
              style={{ width: '100%', justifyContent: 'end' }}
            >
              <Button
                type="primary"
                disabled={!isFormChanged}
                ghost
                onClick={() => handleVerifyWebhook()}
                loading={verifyWebhookMutation.isLoading}
              >
                Test Webhooks
              </Button>
              {isVerifyError && (
                <Popconfirm
                  placement="topLeft"
                  title="Webhook Error"
                  description={<ErrorDescription message={verifyErrorMessage} />}
                  okText="okay"
                  showCancel={false}
                >
                  <Button type="link" icon={<WarningOutlined />} danger />
                </Popconfirm>
              )}
              {isVerifySuccess && (
                <Button type="link" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} />
              )}
            </Space>
          </Panel>
        </Collapse>
        <Divider />
        <Form.Item>
          <Space align="end" direction="horizontal" style={{ width: '100%' }}>
            <Popconfirm
              title="Are you sure? Unsaved changes will be lost."
              okText="Yes"
              open={cancelPopOpenStatus}
              cancelText="No"
              onConfirm={handleConfirmPopConfirm}
              onCancel={handleCancelPopConfirm}
              onOpenChange={handleOpenChange}
            >
              <Button type="primary" ghost>
                Cancel
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              htmlType="submit"
              disabled={!verifiedWebhookStatus}
              loading={createWebhooksMutation.isLoading || editWebhooksMutation.isLoading}
            >
              Save
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </StyledWrapper>
  );
};

export default WebhooksForm;
